import {
	Button,
	Flex,
	Heading,
	Stack,
	Text,
	useColorModeValue,
} from '@chakra-ui/react';
import { createTheme } from '@mui/material/styles';
import { disputService } from 'api/services/disputeService';
import { LoadingSpinner } from 'components/loading/LoadingSpinner';
import ResultMessage from 'components/modal/ResultMessage';
import { ALERT } from 'constant/messages';
import { NAVIGATE } from 'constant/navigation';
import { ITEMS_PER_PAGE } from 'constant/other';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { storeActions } from 'store/store';
import { fetchData, handleCloseModal, handleMessage } from 'utils/hooks';
import { themeMUI } from 'utils/theme';
import History from 'views/client/dispute/components/History';
import Report from 'views/client/dispute/components/Report';

export default function Dispute() {
	const TOKEN = useSelector((state) => state.auth.tokenId);
	const BRANDS = useSelector((state) => state.store.storeAllBrand);
	const DISPUTES = useSelector((state) => state.store.storeAllDispute);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const initialStateFormData = {
		brandId: '',
		buyDate: dayjs(),
		order: '',
		amount: '',
		currency: 'MXN',
		status: 'PENDIENTE',
		notes: '',
	};

	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState('reportar');
	const [currentPage, setCurrentPage] = useState(1);
	const [disputeData, setDisputeData] = useState(DISPUTES || []);
	const [formData, setFormData] = useState(initialStateFormData);
	const [message, setMessage] = useState({
		state: false,
		status: '',
		description: '',
	});

	const textColorPrimary = useColorModeValue('#151615', '#E3E3D7');
	const cardBg = useColorModeValue('gray.100', 'gray.500');
	const iconBg = useColorModeValue('gray.500', 'gray.500');
	const brandColor = useColorModeValue('brand.500', 'gray.100');
	const inputBorder = useColorModeValue('#151615', '#E3E3D7');
	const theme = createTheme(
		themeMUI(
			textColorPrimary,
			textColorPrimary,
			textColorPrimary,
			textColorPrimary
		)
	);

	const handleFocus = () => {
		setMessage({
			state: false,
			status: '',
			description: '',
		});
	};

	const handleSubmit = async () => {
		try {
			setLoading(true);
			const result = await disputService.add(TOKEN, formData);
			if (result.status === 200 && result.data.status === 'SUCCESS') {
				formData.createdAt = new Date();
				dispatch(
					storeActions.setStoreData({
						storeAllDispute: [formData, ...disputeData],
					})
				);
				setDisputeData((prevState) => [formData, ...prevState]);
				setFormData(initialStateFormData);
				handleMessage(
					'success',
					ALERT.SUCCESS_TITLE,
					ALERT.SUCCESS_SUBTITLE,
					setMessage
				);
			} else {
				throw new Error('Dispute creation unsuccessful');
			}
		} catch (error) {
			console.error(error);
			handleMessage(
				'error',
				ALERT.ERROR_TITLE,
				ALERT.ERROR_SUBTITLE,
				setMessage
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		document.querySelector('.scroll-body')?.scrollTo(0, 0);
		const fetchDisputes = async () => {
			fetchData({
				apiCalls: [() => disputService.getAll(TOKEN)],
				setStates: [(data) => setDisputeData(data)],
				setStores: [
					(data) =>
						dispatch(storeActions.setStoreData({ storeAllDispute: data })),
				],
				alertMessages: ALERT,
				setMessage,
				setLoading,
			});
		};

		fetchDisputes();
	}, []);

	useEffect(() => {
		setCurrentPage(1);
	}, []);

	const paginatedData = useMemo(() => {
		return disputeData.slice(
			(currentPage - 1) * ITEMS_PER_PAGE,
			currentPage * ITEMS_PER_PAGE
		);
	}, [activeTab, disputeData, currentPage]);

	const totalPages = Math.ceil(disputeData.length / ITEMS_PER_PAGE);

	return (
		<Flex direction='column' pt={{ sm: '125px', lg: '75px' }}>
			<Heading mb='20px' fontSize='2xl' ms='24px' fontWeight='700'>
				Reportar una disputa
			</Heading>
			<LoadingSpinner loading={loading} />
			<Stack spacing={5}>
				{!loading && (
					<Flex gap={4} mt={4} mb={2} px={6}>
						<Button
							variant={activeTab === 'reportar' ? 'solid' : 'outline'}
							onClick={() => setActiveTab('reportar')}
							bg={activeTab === 'reportar' && 'brand.500'}
							color={activeTab === 'reportar' && 'gray.100'}
						>
							Reportar
						</Button>
						<Button
							variant={activeTab === 'historial' ? 'solid' : 'outline'}
							onClick={() => setActiveTab('historial')}
							bg={activeTab === 'historial' && 'brand.500'}
							color={activeTab === 'historial' && 'gray.100'}
						>
							Mis disputas
						</Button>
					</Flex>
				)}
				{!loading && activeTab === 'reportar' && (
					<Report
						BRANDS={BRANDS}
						formData={formData}
						setFormData={setFormData}
						message={message}
						loading={loading}
						handleFocus={handleFocus}
						handleSubmit={handleSubmit}
						textColorPrimary={textColorPrimary}
						inputBorder={inputBorder}
						cardBg={cardBg}
						brandColor={brandColor}
						theme={theme}
					/>
				)}
				{!loading && activeTab === 'historial' && disputeData.length > 0 && (
					<History
						paginatedData={paginatedData}
						cardBg={cardBg}
						iconBg={iconBg}
						totalPages={totalPages}
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
					/>
				)}
				{!loading && activeTab === 'historial' && disputeData.length === 0 && (
					<Flex
						flexDirection='column'
						p='3rem'
						justifyContent='center'
						alignItems='center'
					>
						<Text textAlign='center'>
							No hay disputas registradas. <br /> ¡Explora nuestras marcas para
							seguir generando cashback!
						</Text>
						<Button
							variant='brand'
							mt='25px'
							onClick={() => navigate(NAVIGATE.BRANDS)}
						>
							Explorar marcas
						</Button>
					</Flex>
				)}
			</Stack>
			<ResultMessage
				isOpen={message.state}
				onClose={() => handleCloseModal(setMessage)}
				message={message}
			/>
		</Flex>
	);
}
